import api from 'services/api';
import { REJECT, array, bool, nullable, numeric, object, oneOf, str, type Received } from 'validation';
import { readResponse } from 'api/shared/readResponse';

// GET /events/:id/occurrences — the unconsumed occurrences behind a Live Events
// card's surplus link. Backend:
// modules/generic_functions/events/getEventOccurrences.js.
//
// Kind is carried on the envelope rather than per row: an event is process or
// file, never a mix, so the modal switches once instead of per item. The row
// shape is the union of both kinds with the inapplicable half null — the two
// column sets are small and disjoint, and a discriminated union per row would
// buy nothing when the envelope already settles it.

export const OCCURRENCE_KINDS = ['process', 'file'] as const;
export type OccurrenceKind = (typeof OCCURRENCE_KINDS)[number];

/** The event the occurrences belong to, for the modal's title. */
export interface OccurrencesEvent {
  readonly event_id: number;
  readonly name: string;
  readonly description: string | null;
  readonly type: string;
  readonly zone_id: number;
  readonly zone_name: string;
}

export interface EventOccurrence {
  /** `event_instance_id` or `file_event_instance_id` — whichever table this came from. */
  readonly occurrence_id: number;
  readonly created_at: string | null;
  // process-kind columns
  readonly raiser_id: number | null;
  readonly raiser_comment: string | null;
  readonly raiser_node_name: string | null;
  // file-kind columns
  readonly file_path: string | null;
  readonly original_file_path: string | null;
  readonly ssh_connection_string: string | null;
  readonly delivery_id: string | null;
}

export interface EventOccurrencesResponse {
  readonly event: OccurrencesEvent;
  readonly kind: OccurrenceKind;
  /** Oldest first — the order "Discard one" draws them down in. */
  readonly occurrences: readonly EventOccurrence[];
  readonly truncated: boolean;
  readonly limit: number;
}

const eventSchema = object<OccurrencesEvent>({
  event_id: numeric(REJECT),
  name: str(),
  description: nullable(str()),
  type: str(),
  zone_id: numeric(),
  zone_name: str(),
});

// The kind-inapplicable columns are absent from the payload rather than null —
// the backend selects a different column list per kind — so each is
// optional-and-nullable collapsed to `nullable`, which treats a missing key as
// drift defaulting to null. That default is the correct value here, and the
// warning it records is not enforced below.
const occurrenceSchema = object<EventOccurrence>({
  occurrence_id: numeric(REJECT),
  created_at: nullable(str()),
  raiser_id: nullable(numeric()),
  raiser_comment: nullable(str()),
  raiser_node_name: nullable(str()),
  file_path: nullable(str()),
  original_file_path: nullable(str()),
  ssh_connection_string: nullable(str()),
  delivery_id: nullable(str()),
});

const responseSchema = object<EventOccurrencesResponse>({
  event: eventSchema,
  kind: oneOf(OCCURRENCE_KINDS, 'process'),
  occurrences: array(occurrenceSchema),
  truncated: bool(),
  limit: numeric(),
});

/**
 * GET `/events/:id/occurrences`. Throws on transport error — including the 404
 * for an event deleted between page load and modal open, which the caller
 * surfaces as a load failure — or on structural validation failure.
 *
 * `occurrence_id` is enforced per row because "Discard one" addresses an
 * occurrence by it: a row whose id defaulted to 0 would target the wrong
 * occurrence, so it is dropped rather than shown.
 */
export const getEventOccurrences = async (eventId: number): Promise<Received<EventOccurrencesResponse>> => {
  const res = await api.get(`/events/${encodeURIComponent(eventId)}/occurrences`);
  return readResponse(res.data, { schema: responseSchema, source: 'getEventOccurrences' });
};
